import React, { useRef } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import useDuperbrowseLink from '../hooks/useDuperbrowseLink';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { getDeviationUrl } from '../../DeviationViews/_helpers';
import {
  useBiDataForDeviationLink,
  ExtraBiDataForDeviationLink,
  ExtraBiDataForTierLink,
} from './useBiDataForDeviationLink';
import { getDeviationEntityId } from '../../redux/normalizr/schemas/deviation';
import { useDuperbrowseDisabled } from '../DuperbrowseDisabledContext';
import { sanitizeUrl } from '@wix/da-url';

export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  deviation: PapiDeviation;
  overrideHref?: string;
  extraBiData?: ExtraBiDataForDeviationLink | ExtraBiDataForTierLink;
  disableScrollRestoration?: boolean;
  canLinkToStash?: boolean;
}

const DeviationLink: React.FC<Props> = ({
  deviation,
  onClick,
  children,
  overrideHref,
  extraBiData,
  disableScrollRestoration,
  canLinkToStash = false,
  target,
  rel,
  ...restProps
}) => {
  const ref = useRef<HTMLElement>(undefined);
  const duperbrowseDisabled = useDuperbrowseDisabled();
  const linkBiData = useBiDataForDeviationLink(deviation, extraBiData);
  const onDuperbrowseClick = useDuperbrowseLink(
    getDeviationEntityId(deviation),
    ref,
    onClick,
    disableScrollRestoration
  );

  if (!deviation.isPublished && !canLinkToStash) {
    return (
      <span className={restProps.className} style={restProps.style}>
        {children}
      </span>
    );
  }

  const linkTarget = duperbrowseDisabled ? '_blank' : target;
  const linkRel = duperbrowseDisabled ? 'noopener noreferrer' : rel;

  return (
    <BiLink
      href={sanitizeUrl(overrideHref || getDeviationUrl(deviation))}
      biData={linkBiData}
      onClick={onDuperbrowseClick}
      // the undefined here is needed to keep react typings from going nuts
      innerRef={node => (ref.current = node || undefined)}
      {...restProps}
      target={linkTarget}
      rel={linkRel}
    >
      {children}
    </BiLink>
  );
};
DeviationLink.displayName = 'DeviationLink';

export default DeviationLink;
